import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";
import { Calendar } from "./style";

// Libs
import Translation from "libs/Translation";
import ModalManager from "libs/ModalManager";

// Component
import Text from "components/common/Text";
import Carousel from "components/common/Carousel";
import HPItem from "./HPItem";
import ServiceItem from "./ServiceItem";
import DateItem from "./DateItem";
import DotLoader from "components/common/DotLoader";
import Button from "components/common/Button";
import Select from "components/common/Select";

// Redux
import { setAppointmentAction, getAPI } from "redux/core/actions";
import { setBookingPageURLAction } from "redux/user/actions";

const FreeTimesCalendar = ({
  doctor,
  showServices,
  webConfig,
  title = false,
  hiddenButton = false,
  handleHidden,
  background,
  borderColor = "white",
  hideBookButton,
  nextStepButton,
  onlineService,
  serviceId,
  healthProviderId,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const [loading, setLoading] = useState(false);
  const [freeTimeLoading, setFreeTimeLoading] = useState();
  const [date, setDate] = useState();
  const [isOnline, setIsOnline] = useState(onlineService ?? false);
  const [services, setServices] = useState([]);
  const [service, setService] = useState();
  const [freeReservationTimes, setFreeReservationTimes] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [allOnlineServices, setAllOnlineServices] = useState([]);
  const [allInPersonServices, setAllInPersonServices] = useState([]);
  const [freeTimes, setFreeTimes] = useState();
  const [firstFree, setFirstFree] = useState();
  const [timesSlots, setTimesSlots] = useState([]);
  const [appointment, setAppointment] = useState();
  const [HP, setHP] = useState(
    healthProviderId
      ? healthProviderId
      : doctor?.HealthProviderProfiles?.length
      ? doctor.HealthProviderProfiles[0]?.HealthProvider
      : null
  );
  const [HPId, setHPId] = useState();
  const [serviceName, setServiceName] = useState();
  const [types, setTypes] = useState([]);

  // One month later date (for free-times window)
  const now = new Date();
  const oneMonthLaterUTC = new Date(now);
  oneMonthLaterUTC.setUTCMonth(oneMonthLaterUTC.getUTCMonth() + 1);
  const endRangeISO = oneMonthLaterUTC.toISOString();

  // --- Fetch FREE TIMES for selected service (Nest) ---
  useEffect(() => {
    if (!service) return;

    setFreeTimeLoading(true);

    dispatch(
      getAPI(
        "getFreeTimes",
        {
          serviceId: service, // required
          startTime: new Date().toISOString(), // optional
          endTime: endRangeISO, // optional
        },
        "nestApi"
      )
    )
      .then((res) => {
        // Normalize to old shape: { TimeSlots: [{ Date, Slots: [{ StartTime, EndTime }] }] }
        const normalized = {
          TimeSlots: (res?.items || []).map((it) => ({
            Date: it?.date,
            Slots: (it?.slots || []).map((s) => ({
              StartTime: s?.startTime,
              EndTime: s?.endTime,
            })),
          })),
        };
        setFreeTimes(normalized);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setFreeTimeLoading(false);
      });
  }, [service]);

  // --- Fetch SERVICES for worker (NODE API) ---
  useEffect(() => {
    if (!doctor?.HealthWorker) return;

    setLoading(true);

    // nodeApi expects: { hpId, lang, HWId }
    const lang = (webConfig?.lang || "fa").toLowerCase();

    dispatch(
      getAPI(
        "getHPServices",
        {
          hpId: HP, // current health provider id
          lang: webConfig?.lang, // keep original casing the nodeApi expects
          HWId: doctor?.HealthWorker,
        },
        "nodeApi"
      )
    )
      .then((res) => {
        // Normalize to keep UI logic unchanged
        const list = Array.isArray(res) ? res : [];
        const normalized = list.map((r) => {
          const localized =
            (r?.Names || []).find(
              (n) =>
                String(n?.Lang || "").toLowerCase() ===
                String(lang || "").toLowerCase()
            )?.Name ||
            r?.Name ||
            "";
          return {
            id: r?._id || r?.id, // UI expects "id"
            Name: localized,
            IsOnline: !!r?.IsOnline,
            HasOnlineScheduling: !!r?.HasOnlineScheduling,
            IsActive: r?.IsActive ?? true,
            HealthProvider: r?.HealthProvider?._id || r?.HealthProvider,
            raw: r,
          };
        });

        setServices(normalized);

        const inPerson = normalized.filter(
          (i) => i.HasOnlineScheduling === true && i.IsOnline === false
        );
        const online = normalized.filter(
          (i) => i.HasOnlineScheduling === true && i.IsOnline === true
        );

        setAllInPersonServices(inPerson);
        setAllOnlineServices(online);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [doctor?.HealthWorker, webConfig?.lang, HP]);

  // Activate online/in-person buttons
  useEffect(() => {
    let serviceTypes = [];

    if (showServices === true) {
      serviceTypes = [
        { id: 0, isOnline: false, label: Translation.t("label.in-person") },
        { id: 1, isOnline: true, label: Translation.t("label.online") },
      ];
    } else {
      const hasInPerson = (allInPersonServices || []).length > 0;
      const hasOnline = (allOnlineServices || []).length > 0;
      if (hasInPerson)
        serviceTypes.push({
          id: 0,
          isOnline: false,
          label: Translation.t("label.in-person"),
        });
      if (hasOnline)
        serviceTypes.push({
          id: 1,
          isOnline: true,
          label: Translation.t("label.online"),
        });
    }

    setTypes(serviceTypes);
  }, [allInPersonServices, allOnlineServices, showServices]);

  useEffect(() => {
    if (types.length) {
      setIsOnline(types[0]?.isOnline);
    }
  }, [types]);

  useEffect(() => {
    setTypes([]);
    setIsOnline(false);

    const selectedHP = doctor?.HealthProviderProfiles?.filter(
      (h) => h.HealthProvider === HP
    );
    if (selectedHP?.length) {
      setHPId(selectedHP[0]?.HealthProvider);
    }
  }, [HP, doctor?.HealthProviderProfiles]);

  // Set first HP as default selected
  useEffect(() => {
    if (healthProviderId) {
      setHP(healthProviderId);
    } else if (doctor?.HealthProviderProfiles?.length) {
      setHP(doctor.HealthProviderProfiles[0]?.HealthProvider);
    }
  }, [doctor?.HealthProviderProfiles, healthProviderId]);

  useEffect(() => {
    const selectedService = services?.filter((s) => s.id === service);
    if (selectedService?.length) {
      setServiceName(selectedService[0]?.Name);
    }
  }, [service, services]);

  // Choose default service by type or external serviceId
  useEffect(() => {
    if (serviceId) {
      setService(serviceId);
      const selected = services?.find((s) => s.id === serviceId);
      setFilteredServices(selected ? [selected] : []);
      return;
    }

    if (isOnline) {
      setFilteredServices(allOnlineServices);
      setService(allOnlineServices?.[0]?.id);
    } else {
      setFilteredServices(allInPersonServices);
      setService(allInPersonServices?.[0]?.id);
    }
  }, [isOnline, serviceId, allInPersonServices, allOnlineServices, services]);

  useEffect(() => {
    handleSetFreeReservationTimes(date);
  }, [date, freeTimes]);

  const handleSetFreeReservationTimes = (d) => {
    if (!d) return;
    const filteredDate = freeTimes?.TimeSlots?.filter((t) => t.Date === d);
    if (filteredDate?.length) {
      setFreeReservationTimes(filteredDate[0].Slots);
    } else {
      setFreeReservationTimes([]);
    }
  };

  // Sorting time slots and pick first free day
  useEffect(() => {
    const dates = freeTimes?.TimeSlots ? [...freeTimes.TimeSlots] : [];
    dates?.sort((a, b) => new Date(a.Date) - new Date(b.Date));
    setTimesSlots(dates);

    if (dates?.length) {
      setDate(dates[0]?.Date);
      handleSetFreeReservationTimes(dates[0]?.Date);

      for (let i = 0; i < dates.length; i++) {
        if (dates[i]?.Slots?.length > 0) {
          setFirstFree(dates[i]?.Date);
          return;
        }
      }
    } else {
      setDate(undefined);
      setFirstFree(undefined);
    }
  }, [freeTimes]);

  const handleSelectHP = (id) => {
    setHP(id);
  };

  const handleSelectService = (name, label, id) => {
    setService(id);
  };

  const handleSelectDate = (d) => {
    setDate(d);
  };

  const handleBookDate = () => {
    const svcObj = services.find((s) => s.id === service);

    dispatch(
      setAppointmentAction({
        HPId: HPId,
        serviceName: serviceName,
        service: { StartTime: appointment.StartTime, Service: svcObj },
        doctor: doctor,
      })
    );
    dispatch(setBookingPageURLAction(router.asPath));

    const qs = `type=${isOnline ? "online" : "in-person"}`;
    if (webConfig.lang !== "fa") {
      router.push(`/get-appointment?${qs}&lang=${webConfig.lang}`);
    } else {
      router.push(`/get-appointment?${qs}`);
    }

    ModalManager.hide();
  };

  return (
    <Calendar
      background={background}
      borderColor={borderColor}
      hiddenButton={hiddenButton}
      spanWidth={types?.length === 2 ? "50%" : "100%"}
      title={title}
    >
      {title && (
        <div className="box padding-0">
          <Text tag="p" className="date-selection">
            {Translation.t("title.get-appointment")}
          </Text>
        </div>
      )}

      {loading ? (
        <div className="loading">
          <DotLoader size={10} color="gray" />
        </div>
      ) : types?.length > 0 ? (
        <>
          <div className="box row-flex padding-0 ">
            {types?.map((t) => (
              <span
                key={t.id}
                onClick={() => setIsOnline(t.isOnline)}
                style={
                  isOnline === t.isOnline
                    ? { border: "1px solid #29BCAB" }
                    : { border: `1px solid ${borderColor}` }
                }
              >
                {t.label}
              </span>
            ))}
          </div>

          {doctor?.HealthProviderProfiles?.length && (
            <div className="box padding-0 ">
              <Carousel
                id="HealthProviders"
                items={doctor.HealthProviderProfiles}
                gap={0}
                renderItem={(item) => (
                  <HPItem
                    borderColor={borderColor}
                    selected={HP}
                    onClick={handleSelectHP}
                    {...item}
                  />
                )}
              />
            </div>
          )}

          <div className="box padding-0 ">
            {filteredServices?.length > 0 ? (
              <>
                {filteredServices.length < 5 ? (
                  <Carousel
                    id="services"
                    items={filteredServices}
                    gap={0}
                    renderItem={(item) => (
                      <ServiceItem
                        borderColor={borderColor}
                        selected={service}
                        onClick={handleSelectService}
                        {...item}
                      />
                    )}
                  />
                ) : (
                  <Select
                    list={filteredServices}
                    value={filteredServices.find((s) => service === s.id)?.Name}
                    handleSelect={handleSelectService}
                    name="service"
                    valueKey="id"
                    labelKey="Name"
                    freeTimeCalendar
                  />
                )}
              </>
            ) : (
              <div className="no-free-time">
                {Translation.t("text.no-services")}
              </div>
            )}
          </div>

          {HP && service && filteredServices.length > 0 ? (
            <div className="box padding-0 ">
              {freeTimeLoading ? (
                <div className="loading">
                  <DotLoader size={10} color="gray" />
                </div>
              ) : (
                <>
                  {timesSlots?.length ? (
                    <Carousel
                      id="freeTimes"
                      items={timesSlots}
                      gap={0}
                      renderItem={(item) => (
                        <DateItem
                          borderColor={borderColor}
                          selected={date}
                          onClick={handleSelectDate}
                          {...item}
                        />
                      )}
                    />
                  ) : (
                    <div className="no-free-time">
                      {Translation.t("text.no-free-time")}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : null}

          {!freeTimeLoading &&
          timesSlots?.length &&
          filteredServices.length > 0 ? (
            <>
              <div className="box padding-16 free-times row-flex">
                {freeReservationTimes?.length > 0 ? (
                  <>
                    {freeReservationTimes?.map((f) => (
                      <div
                        key={f.StartTime}
                        className="item"
                        onClick={() => setAppointment(f)}
                        style={
                          appointment?.StartTime === f.StartTime
                            ? { border: "1px solid #29BCAB" }
                            : { border: `1px solid ${borderColor}` }
                        }
                      >
                        {Translation.time(f.StartTime)}
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="no-free-time-wrapper">
                    <div className="no-free-time">
                      {Translation.t("text.all-times-booked")}
                    </div>
                    <Button
                      onClick={() => setDate(firstFree)}
                      className="view-first-free-time-button"
                    >
                      {Translation.t("label.view-first-free-time")}
                    </Button>
                  </div>
                )}
              </div>

              {hiddenButton || !hideBookButton ? (
                <div className="row-flex button-container">
                  {!hideBookButton && (
                    <Button
                      className="book-button"
                      disabled={!appointment}
                      onClick={handleBookDate}
                    >
                      {Translation.t("label.book-date")}
                    </Button>
                  )}
                  {hiddenButton && (
                    <Button className="close-button" onClick={handleHidden}>
                      {Translation.t("label.close")}
                    </Button>
                  )}
                </div>
              ) : null}

              {nextStepButton && (
                <Button
                  className="next-step-button"
                  disabled={!appointment}
                  onClick={handleBookDate}
                >
                  {Translation.t("label.next-step")}
                </Button>
              )}
            </>
          ) : null}
        </>
      ) : (
        <>
          {doctor?.HealthProviderProfiles?.length > 1 && types?.length > 0 && (
            <div className="box padding-0 ">
              <Carousel
                id="HealthProviders"
                items={doctor.HealthProviderProfiles}
                gap={0}
                renderItem={(item) => (
                  <HPItem
                    borderColor={borderColor}
                    selected={HP}
                    onClick={handleSelectHP}
                    {...item}
                  />
                )}
              />
            </div>
          )}
          <Text tag="p" className="no-free-times">
            {Translation.t("text.not-able-to-book-date")}
          </Text>
        </>
      )}
    </Calendar>
  );
};

export default FreeTimesCalendar;
